/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CardViewDateItemModel } from '@alfresco/adf-core';
import { withBuddhistYear } from '../pipes/thai-date.pipe';

const PATCHED = Symbol('acaBuddhistEra');

/**
 * Makes the dates ADF's metadata card prints — the Properties tab of a
 * document and the info drawer — read in the Buddhist Era like the rest of the
 * application.
 *
 * The card formats each date in `CardViewDateItemModel.transformDate` with a
 * `DatePipe` it creates itself, so there is no provider or pipe to swap; the
 * method is wrapped instead. It is display only: ADF reads it for
 * `displayValue`, while the value it saves and the date picker it opens work
 * from `value`, which stays a Common Era date. `CardViewDatetimeItemModel`
 * inherits the method, so date-times are covered too.
 *
 * Safe to call more than once.
 */
export function showBuddhistEraInAdfCards(): void {
  const prototype = CardViewDateItemModel.prototype as CardViewDateItemModel & { [PATCHED]?: boolean };
  if (prototype[PATCHED]) {
    return;
  }

  const transformDate = prototype.transformDate;
  prototype.transformDate = function (this: CardViewDateItemModel, value: Date | string | number): string {
    const formatted = transformDate.call(this, value);
    const year = new Date(value).getFullYear();
    return Number.isNaN(year) ? formatted : withBuddhistYear(formatted, year);
  };
  prototype[PATCHED] = true;
}
